import { apiFetch } from './client.js';

export type ReconciliationRunStatus = 'COMPLETED' | 'FAILED' | 'PARTIAL';

export interface ReconciliationRun {
  runId: string;
  runDate: string;
  status: ReconciliationRunStatus;
  ledgerNetCents: string;
  partnerStatementNetCents: string;
  breakCents: string;
  currency: string;
  channel: string;
  breakCount: number;
}

export type BreakResolutionStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'ESCALATED';
export type BreakType =
  | 'MISSING_LEDGER_ENTRY'
  | 'MISSING_PARTNER_RECORD'
  | 'AMOUNT_MISMATCH';

export interface ReconciliationBreak {
  breakId: string;
  runId: string;
  breakType: BreakType;
  accountId: string;
  expectedCents: string;
  actualCents: string;
  currency: string;
  referenceId: string;
  resolutionStatus: BreakResolutionStatus;
  openedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNotes?: string;
  ageDays: number;
}

export interface ReconciliationRunDetail extends ReconciliationRun {
  breaks: ReconciliationBreak[];
}

export interface ReconciliationStats {
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  matchRatePct: number;
  totalBreaks: number;
  totalBreakCents: string;
  trend: Array<{ date: string; matchRatePct: number }>;
}

export interface ListRunsParams {
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface ListBreaksParams {
  status?: BreakResolutionStatus;
  aging?: 'fresh' | 'warning' | 'overdue';
  minAmountCents?: string;
  breakType?: BreakType;
  limit?: number;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== '',
  ) as Array<[string, string | number]>;
  if (entries.length === 0) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of entries) qs.set(k, String(v));
  return `?${qs.toString()}`;
}

function paramsToQueryable(
  obj: Record<string, unknown>,
): Record<string, string | number | undefined> {
  const result: Record<string, string | number | undefined> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    result[k] = typeof v === 'number' ? v : String(v);
  }
  return result;
}

export const reconciliationApi = {
  listRuns: (params: ListRunsParams = {}): Promise<ReconciliationRun[]> =>
    apiFetch<ReconciliationRun[]>(
      `/admin/reconciliation/runs${toQuery(paramsToQueryable(params as Record<string, unknown>))}`,
    ),

  getRun: (runId: string): Promise<ReconciliationRunDetail> =>
    apiFetch<ReconciliationRunDetail>(`/admin/reconciliation/runs/${runId}`),

  listBreaks: (params: ListBreaksParams = {}): Promise<ReconciliationBreak[]> =>
    apiFetch<ReconciliationBreak[]>(
      `/admin/reconciliation/breaks${toQuery(paramsToQueryable(params as Record<string, unknown>))}`,
    ),

  resolveBreak: (
    breakId: string,
    body: { resolutionStatus: BreakResolutionStatus; resolutionNotes: string },
  ): Promise<ReconciliationBreak> =>
    apiFetch<ReconciliationBreak>(`/admin/reconciliation/breaks/${breakId}/resolve`, {
      method: 'POST',
      body,
    }),

  stats: (period: 'DAILY' | 'WEEKLY' | 'MONTHLY' = 'DAILY'): Promise<ReconciliationStats> =>
    apiFetch<ReconciliationStats>(
      `/admin/reconciliation/stats${toQuery({ period })}`,
    ),
};
